import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Chip,
  Paper,
  Alert,
  Switch,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Button,
  CircularProgress,
} from "@mui/material";
import { CheckCircle, Cancel, Edit, Save, Close } from "@mui/icons-material";
import { RootState, AppDispatch } from "../../../redux/store";
import { updateOrganizationMemorySchemaConfig } from "../../../redux/actions/organizationActions";
import { clearMemoryConfigError } from "../../../redux/reducers/organizationReducer";

// Memory schema types matching the backend enum
const MEMORY_SCHEMA_TYPES = [
  {
    value: "background",
    label: "Background",
    description: "Background information and context",
  },
  {
    value: "people",
    label: "People",
    description: "Information about people and relationships",
  },
{ value: "events", label: "Events", description: "Events and activities" },
  {
    value: "documents",
    label: "Documents",
    description: "Document-based memories",
  },
  {
    value: "investormemory",
    label: "Investor Memory",
    description: "Investor-specific information",
  },
];

interface MemorySchemaConfigProps {
  organizationId: string;
  config?: {
    restrict_memory_schemas: boolean;
    allowed_schemas: string[];
  };
}

const MemorySchemaConfig: React.FC<MemorySchemaConfigProps> = ({
  organizationId,
  config,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, memoryConfigError } = useSelector(
    (state: RootState) => state.organizations
  );

  const [isEditing, setIsEditing] = useState(false);
  const [restrictSchemas, setRestrictSchemas] = useState(
    config?.restrict_memory_schemas || false
  );
  const [allowedSchemas, setAllowedSchemas] = useState<string[]>(
    config?.allowed_schemas || []
  );

  // Reset form when config changes
  useEffect(() => {
    setRestrictSchemas(config?.restrict_memory_schemas || false);
    setAllowedSchemas(config?.allowed_schemas || []);
  }, [config]);

  const handleSchemaToggle = (schemaValue: string) => {
    setAllowedSchemas((prev) =>
      prev.includes(schemaValue)
        ? prev.filter((s) => s !== schemaValue)
        : [...prev, schemaValue]
    );
  };

  const handleSave = async () => {
    try {
      await dispatch(
        updateOrganizationMemorySchemaConfig({
          organizationId,
          restrict_memory_schemas: restrictSchemas,
          allowed_schemas: allowedSchemas,
        })
      ).unwrap();

      setIsEditing(false);
    } catch {
      // Error handled by Redux state
    }
  };

  const handleCancel = () => {
    // Reset to original values
    setRestrictSchemas(config?.restrict_memory_schemas || false);
    setAllowedSchemas(config?.allowed_schemas || []);
    setIsEditing(false);
  };

  const allowedSet = new Set(allowedSchemas);
  const hasUnsavedChanges =
    restrictSchemas !== (config?.restrict_memory_schemas || false) ||
    JSON.stringify([...allowedSchemas].sort()) !==
      JSON.stringify([...(config?.allowed_schemas || [])].sort());

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">Memory Schema Configuration</Typography>

        {!isEditing ? (
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={() => setIsEditing(true)}
            size="small"
          >
            Edit
          </Button>
        ) : (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} /> : <Save />}
              onClick={handleSave}
              disabled={loading || !hasUnsavedChanges}
              size="small"
            >
              Save
            </Button>
            <Button
              variant="outlined"
              startIcon={<Close />}
              onClick={handleCancel}
              disabled={loading}
              size="small"
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Controls which memory schemas are processed for this organization during
        memory building.
      </Typography>

      {/* Display memory config error if present */}
      {memoryConfigError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => dispatch(clearMemoryConfigError())}
        >
          Error Updating Configuration: {memoryConfigError}
        </Alert>
      )}

      {isEditing ? (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={restrictSchemas}
                onChange={(e) => setRestrictSchemas(e.target.checked)}
                color="primary"
              />
            }
            label="Restrict Memory Schemas"
            sx={{ mb: 2 }}
          />

          {restrictSchemas && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Only the selected memory schemas below will be processed for
                this organization. This helps reduce costs and processing
                overhead.
              </Alert>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Select Allowed Memory Schemas:
              </Typography>

              <FormGroup>
                {MEMORY_SCHEMA_TYPES.map((schema) => (
                  <FormControlLabel
                    key={schema.value}
                    control={
                      <Checkbox
                        checked={allowedSchemas.includes(schema.value)}
                        onChange={() => handleSchemaToggle(schema.value)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          {schema.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {schema.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ mb: 1 }}
                  />
                ))}
              </FormGroup>

              {restrictSchemas && allowedSchemas.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  No schemas selected. This will prevent all memory processing
                  for this organization.
                </Alert>
              )}
            </>
          )}

          {!restrictSchemas && (
            <Alert severity="success">
              All memory schemas will be processed for this organization
              (default behavior).
            </Alert>
          )}
        </>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <Chip
              icon={restrictSchemas ? <CheckCircle /> : <Cancel />}
              label={
                restrictSchemas
                  ? "Memory Schema Restrictions ENABLED"
                  : "No Restrictions (All Schemas Processed)"
              }
              color={restrictSchemas ? "warning" : "success"}
              variant={restrictSchemas ? "filled" : "outlined"}
            />
          </Box>

          {restrictSchemas ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Only the selected memory schemas below are processed for this
                organization. This helps reduce costs and processing overhead.
              </Alert>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Allowed Memory Schemas:
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {MEMORY_SCHEMA_TYPES.map((schema) => {
                  const isAllowed = allowedSet.has(schema.value);
                  return (
                    <Box
                      key={schema.value}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: isAllowed
                          ? "action.hover"
                          : "transparent",
                      }}
                    >
                      <Chip
                        size="small"
                        label={isAllowed ? "Enabled" : "Disabled"}
                        color={isAllowed ? "success" : "default"}
                        variant={isAllowed ? "filled" : "outlined"}
                      />
                      <Box>
                        <Typography
                          variant="body1"
                          fontWeight={isAllowed ? 500 : 400}
                        >
                          {schema.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {schema.description}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {allowedSchemas.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  No schemas selected. This prevents all memory processing for
                  this organization.
                </Alert>
              )}
            </>
          ) : (
            <Alert severity="success">
              All memory schemas are processed for this organization (default
              behavior).
            </Alert>
          )}
        </>
      )}
    </Paper>
  );
};

export default MemorySchemaConfig;
